import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "@hooks/useNotification";
import { useMultipleFileUpload } from "@hooks/index";
import "date-fns";
import moment from "moment-timezone";
import { Controller, useForm } from "react-hook-form";
import "@css/pages/connexion/connexion.css";
import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import {
  Switch,
  Input,
  TextArea,
} from "@components/tailwindComponents";
import {
  MODEL_NAME_EMPLOI_CERTIFICATION,
  CONSTANT_SOCKET_FOR_CERTIFICATION
} from "@helpers/_constants";
import {
  getFileTitle,
  getUpdatedSections
} from "@helpers/_functions";
import {
  DocumentStorage,
  EmployerCertification,
  DataFile
} from "@services/all.services";
import { NotificationUtil } from "@utils/notification.utils";
import { toLocalCertificationDate } from "@utils/certificationDate";
import UploadFile from "../../SharedFormation/components/UploadFile";


const ModalEditCertification = ({
  isOpen,
  certification,
  idEmployer,
  onClickClose,
  isEnterprisePage = false,
  userConnected
}) => {
  const { control, watch, reset, setValue, clearErrors, handleSubmit, formState: { dirtyFields } } =
    useForm({
      mode: "onSubmit",
      reValidateMode: "onChange",
      defaultValues: {
        certificationTitle: "",
        DateStart: null,
        DateEnd: null,
        DateAlerte: null,
        certificationNote: "",
        neverEnd: false
      }
    });
  const { showError, showInfo, showSuccess } = useNotification();
  const { t } = useTranslation("pages.employés.parcours&Certifications");

  const [isSaving, setIsSaving] = useState(false);

  const {
    oldFiles,
    handleDeleteOldFiles,
    removeOldFiles,
    toDeleteOldFiles,
    allFileDone,
    resetData,
    handleUploadStatusChange,
    resetInputFiles,
  } = useMultipleFileUpload({
    defaultFiles: certification?.file,
    onDeleteOldFile: () => { },
  });

  useEffect(() => {
    if (!isOpen) {
      reset({
        certificationTitle: certification?.title || "",
        DateStart: toLocalCertificationDate(certification?.obtainedDate),
        DateEnd: toLocalCertificationDate(certification?.endValidityDate),
        DateAlerte: certification?.alertDate
          ? moment(certification?.alertDate).toDate()
          : null,
        certificationNote: certification?.note || "",
        neverEnd: certification?.endValidityNever
      });
      clearErrors();
      setIsSaving(false);
      removeOldFiles();
      resetData();
    } else {
      resetInputFiles();
    }
  }, [isOpen, certification]);

  const onError = useCallback(
    (errors) =>
      Object.values(errors).some(({ type }) => type === "required")
        ? showError("field_obligatory")
        : showError("verification_two_object", {
          object1: "endDate",
          operator: "superiour",
          object2: "startDate"
        }),
    []
  );  

  const onSuccess = async (formValue) => {
    if (isSaving) {
      showInfo("Véuillez patienter svp, l'ajout est en cours");
      return;
    }
    setIsSaving(true);
    try {
      const enterpriseData = JSON.parse(localStorage.getItem("Enterprise"));

      let _certification = null,
        resNotif = null,
        dataCertification = {
          title: formValue.certificationTitle,
          obtainedDate: moment(formValue.DateStart).format("YYYY-MM-DD"),
          endValidityDate: formValue.neverEnd
            ? null
            : moment(formValue.DateEnd).format("YYYY-MM-DD"),
          endValidityNever: formValue.neverEnd,
          alertDate: formValue.neverEnd || !formValue.DateAlerte
            ? null
            : moment(formValue.DateAlerte).format("YYYY-MM-DD"),
          note: formValue.certificationNote,
          User: localStorage.getItem("idAdminEnterprise"),
          Employer: idEmployer,
          Enterprise: enterpriseData?._id
        };
        if (toDeleteOldFiles?.length) {
          await removeOldFiles();
        }

      if (certification?._id) {
        dataCertification._id = certification._id;
        _certification = (
          await EmployerCertification.updateById(dataCertification)
        )?.data?.data;
      } else {
        _certification = await EmployerCertification.insert(dataCertification);
      }

      if (_certification === "ALREADY_EXISTS") {
        showInfo("already_exists", {
          object: "existEmploiCertification",
          object2: dataCertification.title
        });
        setIsSaving(false);
        return;
      }

      if (!_certification?.success) {
        return showError("error");
      }
      const certificationData = _certification?.data;
      const certificationFiles = formValue?.files;

      // Save certification file
      if (
        certificationFiles?.length &&
        enterpriseData?._id &&
        certificationData?._id
      ) {
        let tempKey;
        try {
          tempKey = (await DocumentStorage.getParentByKey({
            key: btoa("Certifications/"),
            employerId: idEmployer
          }))?.data?.data;
        } catch(error) { 
          console.log("error getting parentKey: ", error.message) 
        }
        if (!tempKey) {
          tempKey = {
            key: "Certifications/",
            originalName: "Certifications",
            wasabiName: "Certifications",
            type: "folder",
            isReadWrite: false,
            isVisible: false,
            employerId: idEmployer,
            entrepriseId: enterpriseData._id,
            isRestrictedAccess: false
          };
          DocumentStorage.insert(tempKey).catch((error) => {
            console.error(error);
          });
        }
        for (const key in certificationFiles) {
          const file = certificationFiles[key];
          const certificationFileData = new FormData();
          certificationFileData.append("enterpriseId", enterpriseData._id);
          certificationFileData.append(
            "enterpriseName",
            enterpriseData.enterpriseName
          );
          certificationFileData.append("source", certificationData._id);
          certificationFileData.append(
            "sourceModel",
            MODEL_NAME_EMPLOI_CERTIFICATION
          );
          certificationFileData.append("files", file);
          certificationFileData.append("EmployerId", idEmployer);
          if (Number(key) === certificationFiles.length - 1) {
            certificationFileData.append(
              "emit",
              JSON.stringify({
                msg: CONSTANT_SOCKET_FOR_CERTIFICATION,
                data: enterpriseData._id
              })
            );
          }
          const insertFileResponse = await DataFile.insert(
            certificationFileData
          );

          if (!insertFileResponse?.data?.success) {
            return showError("error");
          } else {
            let response = insertFileResponse?.data?.data?.[0];
            let fileName = getFileTitle(response.key);

            if (response) {
              const dataFolder = {
                key: `Certifications/${fileName}`,
                originalName: file.name,
                wasabiName: file.name,
                type: "file",
                isReadWrite: false,
                isVisible: tempKey.isVisible,
                isRestrictedAccess: tempKey.isRestrictedAccess,
                employerId: idEmployer,
                entrepriseId: enterpriseData._id,
                datafile: response._id
              };
              await DocumentStorage.insert(dataFolder).catch((error) => {
                console.error(error);
              });
            }
          }
        }
      }

      // if (toDeleteOldFiles?.length) {
      //   await removeOldFiles();
      // }

      if (certification?._id) {
        showSuccess("changes_saved");
        resNotif =
          NotificationUtil.emploieCertificationUpdate(certificationData);
      } else {
        showSuccess("creation_action_success", {
          object: "certification"
        });
        resNotif = NotificationUtil.emploieCertificationAdd(certificationData);
      }

      if (!isEnterprisePage) {
        const updatedSections = getUpdatedSections(dirtyFields, {
          certificationTitle: "DCertFormationSectionLicensesAndCertifications",
          DateStart: "DCertFormationSectionLicensesAndCertifications",
          DateEnd: "DCertFormationSectionLicensesAndCertifications",
          DateAlerte: "DCertFormationSectionLicensesAndCertifications",
          certificationNote: "DCertFormationSectionLicensesAndCertifications",
          neverEnd: "DCertFormationSectionLicensesAndCertifications",
          files: "DCertFormationSectionLicensesAndCertifications"
        });
        if (updatedSections?.length) {
          const employeeData = {
            _id: certificationData._id,
            employerId: certificationData.Employer || idEmployer,
            idEmployeSalary: userConnected?.salaryId,
            ...certificationData
          };
          resNotif = NotificationUtil.employeeFileUpdate(
            employeeData,
            "DListCertFormation",
            updatedSections[0]
          );
        }
        reset(formValue);
      }
      if (resNotif?.data?.deniedNotification) showInfo("ATTENTION");
      onClickClose();
    } catch (err) {
      console.error(err);
      showError("error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalIndex
      title={
        certification?._id
          ? t("Modifier la certification")
          : t("Ajouter une certification")
      }
      isOpen={isOpen}
      onClickClose={onClickClose}
      isLoading={isSaving}
      mustWaiting={!allFileDone}
      buttonOptions={{
        click: handleSubmit(onSuccess, onError),
        color: "green",
        title: t("Enregistrer")
      }}
      buttonCancelOptions={{
        click: () => onClickClose(),
        color: "outlined",
        title: t("Annuler")
      }}
    >
      <div className="!arh-px-0">
        <div>
          <Controller
            control={control}
            name="certificationTitle"
            rules={{ required: true }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <Input
                label={t("Titre de la certification")}
                placeholder={t("Titre de la certification")}
                value={value}
                onChange={onChange}
                name={name}
                invalid={invalid}
              />
            )}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <Controller
            control={control}
            name="DateStart"
            rules={{
              required: true,
              validate: (value) =>
                watch("neverEnd") ||
                !watch("DateEnd") ||
                moment(watch("DateEnd")).isAfter(value)
            }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <>
                <label
                  className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                            ${invalid
                      ? " arh-text-[#F60A20] "
                      : " arh-text-black "
                    }
                        `}
                >
                  {t("Date d'obtention")}
                </label>
                <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation position_relative">
                  <DefaultDatePicker
                    className="datepickerDropdown GreenThemeSelect"
                    name={name}
                    placeholder={t("Date d'obtention")}
                    dateValue={value}
                    setDateValue={onChange}
                    invalid={invalid}
                  />
                </div>
              </>
            )}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <Controller
            control={control}
            name="DateEnd"
            rules={{
              required: !watch("neverEnd"),
              validate: (value) =>
                watch("neverEnd") ||
                !watch("DateStart") ||
                moment(value).isAfter(watch("DateStart"))

            }}
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <>
                <label
                  className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                            ${invalid
                      ? " arh-text-[#F60A20] "
                      : " arh-text-black "
                    }
                        `}
                >
                  {t("Date de fin de validité")}
                </label>
                <div className="arhDatePicker employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation position_relative">
                  <DefaultDatePicker
                    className="datepickerDropdown GreenThemeSelect"
                    name={name}
                    readOnly={watch("neverEnd")}
                    placeholder={t("Date de fin de validité")}
                    dateValue={value}
                    setDateValue={onChange}
                    invalid={invalid}
                  />
                </div>
              </>
            )}
          />
        </div>
        <div className="arh-w-full arh-flex arh-justify-end arh-items-center arh-mb-4 arh-mt-2">
          <span className="arh-mr-2 arh-font-poppins arh-font-normal arh-text-xs arh-text-darkColor">
            {t("Jamais")}
          </span>
          <Controller
            control={control}
            name="neverEnd"
            render={({ field: { onChange, value } }) => (
              <Switch
                color="arh-bg-greenColor"
                rotate
                checked={value}
                handleChange={(value) => {
                  onChange(value);
                  if (value) {
                    setTimeout(() => {
                      setValue("DateEnd", null, { shouldValidate: true });
                      setValue("DateAlerte", null, { shouldValidate: true });
                    });
                  }
                }}
              />
            )}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <Controller
            control={control}
            name="DateAlerte"
            render={({
              field: { onChange, value, name },
              fieldState: { invalid }
            }) => (
              <>
                <label
                  className={` arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium 
                            ${invalid
                      ? " arh-text-[#F60A20] "
                      : " arh-text-black "
                    }
                        `}
                >
                  {t("Définir une alerte")}
                </label>
                <div className="arhField employerThemeDatePicker employerSvgAdd calendar_with_btn position_relative">
                  <DefaultDatePicker
                    className="datepickerDropdown GreenThemeSelect"
                    name={name}
                    placeholder={t("Définir une alerte")}
                    dateValue={value}
                    setDateValue={onChange}
                    invalid={invalid}
                    readOnly={watch("neverEnd")}
                  />
                </div>
              </>
            )}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <UploadFile
            t={t}
            control={control}
            oldFiles={oldFiles}
            isDefaultOpen={certification?.file?.length > 0}
            handleUploadStatusChange={handleUploadStatusChange}
            handleDeleteOldFiles={handleDeleteOldFiles}
          />
        </div>
        <div className="arh-mt-2 arh-w-full">
          <Controller
            control={control}
            name="certificationNote"
            render={({ field: { onChange, value, name } }) => (
              <TextArea
                label={t("Notes")}
                placeholder={t("Notes")}
                value={value}
                onChange={onChange}
                name={name}
              />
            )}
          />
        </div>
      </div>
    </ModalIndex>
  );
};

export default memo(ModalEditCertification);